import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { Itinerary } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { ExternalLink, Copy, Check, Globe, Lock, Unlock, Shield } from 'lucide-react';
import { ConfirmationModal } from './ConfirmationModal';
import { generateSlug } from '../utils/slugGenerator';

interface ItineraryListProps {
  onEdit: (itinerary: Itinerary) => void;
  onDelete: (id: number) => void;
  onCreate: () => void;
}

export const ItineraryList: React.FC<ItineraryListProps> = ({
  onEdit,
  onDelete,
  onCreate,
}) => {
  const { t } = useLanguage();
  const [itineraries, setItineraries] = useState<Itinerary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedEndpoint, setCopiedEndpoint] = useState<number | null>(null);
  const [copiedWebsite, setCopiedWebsite] = useState<number | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    itinerary: Itinerary | null;
    loading: boolean;
  }>({
    isOpen: false,
    itinerary: null,
    loading: false,
  });

  const loadItineraries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getMyItineraries();
      setItineraries(response.data);
    } catch (err) {
      setError(t('itineraries.failedToFetch'));
      console.error('Error loading itineraries:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadItineraries();
  }, [loadItineraries]);

  const handleDeleteClick = (itinerary: Itinerary) => {
    setDeleteModal({
      isOpen: true,
      itinerary,
      loading: false,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.itinerary) return;

    setDeleteModal(prev => ({ ...prev, loading: true }));
    
    try {
      await apiService.deleteItinerary(deleteModal.itinerary.id);
      setItineraries(itineraries.filter(itinerary => itinerary.id !== deleteModal.itinerary!.id));
      setDeleteModal({
        isOpen: false,
        itinerary: null,
        loading: false,
      });
    } catch (err) {
      setError(t('itineraries.failedToDelete'));
      console.error('Error deleting itinerary:', err);
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({
      isOpen: false,
      itinerary: null,
      loading: false,
    });
  };

  const getPublicEndpointUrl = (itineraryId: number): string => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    return `${baseUrl}/api/v1/itineraries/${itineraryId}`;
  };

  const getPublicWebsiteUrl = (itinerary: Itinerary): string => {
    const slug = generateSlug(itinerary.title);
    return `https://app.zetaguides.info?itinerary=${slug}`;
  };

  const copyEndpointUrl = async (itineraryId: number) => {
    const url = getPublicEndpointUrl(itineraryId);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedEndpoint(itineraryId);
      setTimeout(() => setCopiedEndpoint(null), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const copyWebsiteUrl = async (itinerary: Itinerary) => {
    const url = getPublicWebsiteUrl(itinerary);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedWebsite(itinerary.id);
      setTimeout(() => setCopiedWebsite(null), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <button
                onClick={loadItineraries}
                className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
              >
                {t('common.tryAgain')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">{t('itineraries.myItineraries')}</h2>
        <button
          onClick={onCreate}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
        >
          {t('itineraries.createNew')}
        </button>
      </div>

      {itineraries.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('itineraries.noItineraries')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('itineraries.createFirstItinerary')}</p>
          <div className="mt-6">
            <button
              onClick={onCreate}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t('itineraries.createNew')}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
          {itineraries.map((itinerary) => (
            <div key={itinerary.id} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900 truncate">
                    {itinerary.title}
                  </h3>
                  <div className="flex items-center space-x-2">
                    {/* Visibility Badge */}
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      itinerary.visibility === 'public' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-orange-100 text-orange-800'
                    }`}>
                      {itinerary.visibility === 'public' ? (
                        <>
                          <Unlock className="h-3 w-3 mr-1" />
                          Public
                        </>
                      ) : (
                        <>
                          <Lock className="h-3 w-3 mr-1" />
                          Restricted
                        </>
                      )}
                    </span>
                    {/* Version Badge */}
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      v{itinerary.version}
                    </span>
                  </div>
                </div>
                
                {/* Strategy Badge (only show if restricted) */}
                {itinerary.visibility === 'restricted' && (() => {
                  // Parse visibility_strategy from comma-separated string or array
                  let strategies: string[] = [];
                  
                  try {
                    if (itinerary.visibility_strategy) {
                      if (Array.isArray(itinerary.visibility_strategy)) {
                        strategies = itinerary.visibility_strategy
                          .map(s => String(s).trim())
                          .filter(s => s.length > 0);
                      } else if (typeof itinerary.visibility_strategy === 'string') {
                        strategies = itinerary.visibility_strategy
                          .split(',')
                          .map(s => String(s).trim())
                          .filter(s => s.length > 0);
                      }
                    }
                  } catch (error) {
                    console.error('Error parsing visibility_strategy:', error);
                    strategies = [];
                  }
                  
                  return strategies.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {strategies.map((strategy, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                          <Shield className="h-3 w-3 mr-1" />
                          {strategy}
                        </span>
                      ))}
                    </div>
                  );
                })()}
                
                {itinerary.description && (
                  <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                    {itinerary.description}
                  </p>
                )}

                <div className="mt-4 space-y-2">
                  <div className="flex items-center text-sm text-gray-500">
                    <svg className="flex-shrink-0 mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {itinerary.data.audioSteps.length} steps
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500">
                    <svg className="flex-shrink-0 mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Updated {new Date(itinerary.updated_at).toLocaleDateString()}
                  </div>
                </div>

                {/* Public Endpoint Section */}
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-600 flex items-center">
                      <ExternalLink className="h-3 w-3 mr-1" />
                      {t('itineraries.fields.publicEndpoint')}
                    </span>
                    <button
                      onClick={() => copyEndpointUrl(itinerary.id)}
                      className="flex items-center text-xs text-blue-600 hover:text-blue-800 transition-colors"
                      title="Copy endpoint URL"
                    >
                      {copiedEndpoint === itinerary.id ? (
                        <>
                          <Check className="h-3 w-3 mr-1" />
                          {t('itineraries.fields.copied')}
                        </>
                      ) : (
                        <>
                          <Copy className="h-3 w-3 mr-1" />
                          {t('itineraries.fields.copyEndpoint')}
                        </>
                      )}
                    </button>
                  </div>
                  <div className="text-xs text-gray-700 font-mono bg-white p-2 rounded border break-all">
                    {getPublicEndpointUrl(itinerary.id)}
                  </div>
                </div>

                {/* Public Website Section */}
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-600 flex items-center">
                      <Globe className="h-3 w-3 mr-1" />
                      {t('itineraries.fields.publicWebsite')}
                    </span>
                    <button
                      onClick={() => copyWebsiteUrl(itinerary)}
                      className="flex items-center text-xs text-blue-600 hover:text-blue-800 transition-colors"
                      title={t('itineraries.fields.copyWebsiteUrl')}
                    >
                      {copiedWebsite === itinerary.id ? (
                        <>
                          <Check className="h-3 w-3 mr-1" />
                          {t('itineraries.fields.copied')}
                        </>
                      ) : (
                        <>
                          <Copy className="h-3 w-3 mr-1" />
                          {t('itineraries.fields.copyWebsite')}
                        </>
                      )}
                    </button>
                  </div>
                  <div className="text-xs text-gray-700 font-mono bg-white p-2 rounded border break-all">
                    {getPublicWebsiteUrl(itinerary)}
                  </div>
                </div>

                <div className="mt-4 flex space-x-2">
                  <button
                    onClick={() => onEdit(itinerary)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-sm font-medium"
                  >
                    {t('itineraries.edit')}
                  </button>
                  <button
                    onClick={() => handleDeleteClick(itinerary)}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md text-sm font-medium"
                  >
                    {t('itineraries.delete')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('itineraries.confirmDelete')}
        message={deleteModal.itinerary ? 
          `${t('itineraries.confirmDelete')} "${deleteModal.itinerary.title}"?` : 
          t('itineraries.confirmDelete')
        }
        confirmText={t('itineraries.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        loading={deleteModal.loading}
      />
    </div>
  );
};
